import { useEffect, useMemo, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { fetchExams, fetchWidgetCopy, subscribeStudent, trackWidgetEvent, recognizeStudent } from "@/lib/studentsApi";
import { ReturningCard, rememberStudent, recallStudent, forgetStudent } from "@/components/students/ReturningCard";
import { useEmbedFrame } from "@/components/students/useEmbedFrame";
import { SubscribeForm } from "@/components/students/SubscribeForm";
import { SubscribeSuccess } from "@/components/students/SubscribeSuccess";

/** Public, auth-free page embedded on entrancezone.com via the [ez_subscribe] shortcode (iframe). */
export default function SubscribeEmbed() {
  const { params, sessionId, rootRef, inIframe } = useEmbedFrame();
  const [taxonomy, setTaxonomy] = useState(null);
  const [copy, setCopy] = useState(null);
  const [result, setResult] = useState(null);
  const [me, setMe] = useState(undefined); // undefined = checking, null = new visitor
  const [notice, setNotice] = useState(null);
  const started = useRef(false);

  useEffect(() => {
    fetchExams().then(setTaxonomy).catch(() => setTaxonomy({ groups: [], exams: [], class_levels: [] }));
    fetchWidgetCopy(params.post_id || undefined, params.exam || undefined).then(setCopy).catch(() => setCopy({}));
    const known = recallStudent();
    if (known?.student_id && known?.return_token) {
      recognizeStudent({ student_id: known.student_id, return_token: known.return_token, exam_hint: params.exam || undefined,
        source: { page_url: params.page_url, page_title: params.page_title, post_id: params.post_id, site: "entrancezone.com" } })
        .then((r) => setMe({ ...r, return_token: known.return_token })).catch(() => { forgetStudent(); setMe(null); });
    } else setMe(null);
    trackWidgetEvent({ event: "widget_viewed", session_id: sessionId, page_url: params.page_url, exam_hint: params.exam });
  }, [sessionId, params]);

  const examHint = (copy && copy.exam) || params.exam;
  const initialExams = useMemo(() => {
    if (!taxonomy || !examHint) return [];
    const hint = examHint.toLowerCase();
    const fine = taxonomy.exams.find((e) => e.name.toLowerCase() === hint);
    if (fine) return [fine.name];
    const fam = (copy && copy.exam_family || "").toLowerCase();
    const grp = taxonomy.groups.find((g) => g.label.toLowerCase().includes(hint) || g.key.toLowerCase() === hint)
      || taxonomy.groups.find((g) => hint.split(/[\s/]+/).includes(g.key.toLowerCase()))
      || (fam && taxonomy.groups.find((g) => g.key.toLowerCase() === fam));
    return grp ? [grp.label] : [];
  }, [taxonomy, examHint, copy]);

  const onStart = () => {
    if (started.current) return;
    started.current = true;
    trackWidgetEvent({ event: "subscription_started", session_id: sessionId, page_url: params.page_url });
  };

  const onSubmit = async (f) => {
    const r = await subscribeStudent({
      ...f, session_id: sessionId,
      source: { page_url: params.page_url, page_title: params.page_title, post_id: params.post_id, utm_source: params.utm_source, utm_medium: params.utm_medium, utm_campaign: params.utm_campaign, exam_hint: params.exam, site: "entrancezone.com" },
    });
    if (r.status === "created" || r.status === "existing") { setResult(r); rememberStudent(r); }
    else setNotice(r.message || "Please check your details and try again.");
  };

  const onShare = () => trackWidgetEvent({ event: "share_clicked", session_id: sessionId, page_url: params.page_url, exam_hint: params.exam });

  return (
    <div ref={rootRef} className="font-sans" data-testid="subscribe-embed">
      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.06),0_12px_32px_-16px_rgba(15,23,42,0.25)] sm:p-6">
        <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-amber-200/40 blur-2xl" />
        {!result && !me && (
          <div className="mb-5">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-indigo-700"><Bell className="h-3.5 w-3.5" /> EntranceZone alerts</div>
            <h2 className="mt-1.5 font-serif text-lg font-semibold leading-snug text-slate-900 md:text-xl" data-testid="widget-headline" data-variant={copy?.variant || "generic"}>
              {copy?.headline || (params.exam ? `Get ${params.exam} updates on WhatsApp` : "Never miss an exam update")}
            </h2>
            <p className="mt-1 text-sm text-slate-600" data-testid="widget-sub">{copy?.sub || "Official date changes, admit cards and results on WhatsApp — plus free AI rank & college prediction."}</p>
          </div>
        )}
        {notice && <p className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="sub-notice">{notice}</p>}
        {!taxonomy || copy === null || me === undefined ? <div className="h-40 animate-pulse rounded-lg bg-slate-100" data-testid="embed-loading" /> :
          me ? <ReturningCard me={me} token={me.return_token} sessionId={sessionId} cta={params.cta} onShare={onShare} onReset={() => { forgetStudent(); setMe(null); }}
                 source={{ page_url: params.page_url, page_title: params.page_title, post_id: params.post_id, exam_hint: params.exam, site: "entrancezone.com" }} /> :
          result ? <SubscribeSuccess result={result} inIframe={inIframe} cta={params.cta} shareUrl={params.page_url} exam={result.exams?.[0] || examHint} onShare={onShare} /> :
            <SubscribeForm taxonomy={taxonomy} initialExams={initialExams} onSubmit={onSubmit} onStart={onStart} compact={params.compact} />}
        <p className="mt-4 text-[11px] text-slate-400">By subscribing you agree to receive exam updates from EntranceZone. Your number is never shared.</p>
      </div>
    </div>
  );
}
